import { Link } from "react-router-dom";
import { CATEGORY_LABELS } from "../lib/constants";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// The template signature: a category kicker is a tinted indigo badge chip.
function Kicker({ children, className = "" }) {
    return <span className={`chip ${className}`}>{children}</span>;
}

function Meta({ author, published, className = "" }) {
    return (
        <div className={`flex items-center gap-2 font-mono text-[11px] text-dim ${className}`}>
            {author && <span className="truncate max-w-[170px]">{author}</span>}
            {author && <span className="w-1 h-1 rounded-full bg-line-strong shrink-0" />}
            <span className="shrink-0">{timeAgo(published)}</span>
        </div>
    );
}

export function ArticleCard({ article, variant = "default" }) {
    if (!article) return null;
    const label = CATEGORY_LABELS[article.category] || article.category;

    // Hero: the same white card, scaled up. No scrim, no inset copy — on a
    // white product surface the image stays an image and the type stays legible.
    if (variant === "hero") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`hero-article-${article.id}`}
                className="group block animate-fade-up rounded-xl border border-line bg-surface shadow-card overflow-hidden transition-[border-color,box-shadow] duration-200 hover:border-accent/45 hover:shadow-lift"
            >
                <div className="aspect-[16/10] sm:aspect-video overflow-hidden bg-surface-raised">
                    {article.thumbnail && (
                        <img
                            src={article.thumbnail}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                    )}
                </div>
                <div className="p-5 sm:p-7">
                    <Kicker>{label}</Kicker>
                    <h2 className="font-display text-2xl sm:text-[2rem] lg:text-[2.35rem] font-extrabold leading-[1.12] tracking-[-0.03em] mt-3 text-foreground group-hover:text-accent transition-colors duration-150">
                        {article.title}
                    </h2>
                    {article.excerpt && (
                        <p className="mt-3 text-[15px] text-dim max-w-2xl leading-relaxed line-clamp-2">
                            {article.excerpt}
                        </p>
                    )}
                    <Meta author={article.author} published={article.published_at} className="mt-5 pt-5 border-t border-line" />
                </div>
            </Link>
        );
    }

    // Compact: a list row that highlights as a soft tinted block on hover.
    if (variant === "compact") {
        return (
            <Link
                to={`/article/${article.id}`}
                data-testid={`article-card-${article.id}`}
                className="group flex gap-3.5 p-3 -mx-3 rounded-lg hover:bg-surface-raised transition-colors duration-150"
            >
                <div className="flex-1 min-w-0">
                    <Kicker className="!text-[10px]">{label}</Kicker>
                    <h3 className="font-display text-[15px] font-semibold leading-snug tracking-[-0.012em] mt-1.5 text-foreground group-hover:text-accent transition-colors duration-150 line-clamp-3">
                        {article.title}
                    </h3>
                    <span className="font-mono text-[11px] text-dim mt-1.5 block">{timeAgo(article.published_at)}</span>
                </div>
                {article.thumbnail && (
                    <div className="w-[84px] h-[68px] shrink-0 overflow-hidden rounded-md border border-line bg-surface-raised">
                        <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                )}
            </Link>
        );
    }

    // Default: white card, hairline border, 14px radius, resting card shadow.
    // Hover raises it 2px, deepens the shadow and turns the border indigo.
    return (
        <Link
            to={`/article/${article.id}`}
            data-testid={`article-card-${article.id}`}
            className="group flex flex-col animate-fade-up rounded-xl border border-line bg-surface shadow-card overflow-hidden transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-accent/45 hover:shadow-lift"
        >
            <div className="aspect-[3/2] overflow-hidden bg-surface-raised">
                {article.thumbnail ? (
                    <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="w-full h-full bg-surface-raised" />
                )}
            </div>
            <div className="flex flex-col flex-1 p-5">
                <div className="mb-4">
                    <Kicker className="!text-[10px]">{label}</Kicker>
                    <h3 className="font-display text-[17px] font-bold leading-snug tracking-[-0.018em] mt-2.5 text-foreground group-hover:text-accent transition-colors duration-150">
                        {article.title}
                    </h3>
                    {article.excerpt && (
                        <p className="mt-2 text-sm text-dim leading-relaxed line-clamp-3">{article.excerpt}</p>
                    )}
                </div>
                <Meta author={article.author} published={article.published_at} className="mt-auto pt-4 border-t border-line" />
            </div>
        </Link>
    );
}

export { timeAgo };
